import React from 'react';
import styled from 'styled-components';
import { OpenOrder } from '../../types/order';
import { SymbolPrecisionsResponse } from '../../services/infoService'; // Import the correct type
import { formatDateTime, formatPrice, formatSize } from '../../utils/formatters'; // Import new formatters
import { Button } from '../common/Button'; // Assuming a common Button component exists

// Basic Table Styling (can be moved to common styles)
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
  color: #ccc;
  th, td {
    padding: 0.4rem 0.6rem;
    text-align: left;
    border-bottom: 1px solid #333;
  }
  th {
    color: #aaa;
    font-weight: bold;
    background-color: #2a2a2a;
    position: sticky;
    top: 0; /* Stick header to top if container scrolls */
  }
  tbody tr:hover {
    background-color: #252525;
  }
`;

const SideText = styled.span<{ side: 'buy' | 'sell' }> `
  color: ${({ side }) => (side === 'buy' ? '#10b981' : '#ef4444')};
  font-weight: bold;
  text-transform: uppercase;
`;

// Add some style for the actions cell if needed
const ActionsCell = styled.td`
  text-align: center; // Center the button
`;

interface OpenOrdersTableProps {
  orders: OpenOrder[];
  symbolPrecisions: SymbolPrecisionsResponse; // Use the imported type
  onCancelOrder?: (symbol: string, orderId: string) => void; // Optional cancel handler
}

const OpenOrdersTable: React.FC<OpenOrdersTableProps> = ({ orders, symbolPrecisions, onCancelOrder }) => {
  if (!orders || orders.length === 0) {
    return null; // Or render an empty state message if preferred
  }

  // Helper function to get precision, with fallback
  const getPrecision = (symbol: string) /*: SymbolPrecisionDetail */ => {
    return symbolPrecisions[symbol] || { price_decimals: 2, size_decimals: 3 }; 
  };

  return (
    <div style={{ maxHeight: '300px', overflowY: 'auto' }}> {/* Example scrollable container */}
      <Table>
        <thead>
          <tr>
            <th>Symbol</th>
            <th>Side</th>
            <th>Type</th>
            <th>Price</th>
            <th>Size</th>
            <th>Filled</th>
            <th>Created</th>
            <th>Reduce</th>
            <th>Post</th>
            <th>Actions</th> {/* Add Actions header */}
          </tr>
        </thead>
        <tbody>
          {orders.map((order) => {
            const precision = getPrecision(order.symbol);
            const handleCancelClick = () => {
              if (onCancelOrder) {
                // Provide default empty string if order_id is missing, though it shouldn't be
                onCancelOrder(order.symbol || '', order.order_id || ''); 
              }
            };
            return (
              <tr key={order.order_id}>
                <td>{order.symbol}</td>
                <td><SideText side={order.side}>{order.side}</SideText></td>
                <td>{order.order_type}</td>
                <td>{formatPrice(order.price, precision.price_decimals)}</td>
                <td>{formatSize(order.size, precision.size_decimals)}</td>
                <td>{formatSize(order.filled_size, precision.size_decimals)}</td>
                <td>{formatDateTime(order.timestamp, 'compact')}</td>
                <td>{order.reduce_only ? 'Yes' : 'No'}</td>
                <td>{order.post_only ? 'Yes' : 'No'}</td>
                <ActionsCell>
                  <Button 
                    size="small" 
                    variant="danger" // Use a danger variant for cancellation
                    onClick={handleCancelClick}
                    disabled={!onCancelOrder} // Disable if no handler provided
                  >
                    Cancel
                  </Button>
                </ActionsCell>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};

export default OpenOrdersTable; 